/**
 * Utility functions for replacing dynamic tags in text content
 */
import DOMPurify from "dompurify";

interface TagReplacements {
  privacyLink?: string;
  termsOfUseLink?: string;
  organizationName?: string;
}

/**
 * Sanitizes user input to prevent XSS attacks
 * Only allows specific safe HTML tags and attributes
 */
const sanitizeInput = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["a", "br", "strong", "em", "b", "i", "u", "span"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
    ALLOW_DATA_ATTR: false,
  });
};

/**
 * Replaces dynamic tags in a string with actual values
 * Supported tags:
 * - {yyyy} - Current year
 * - {mm/dd/yyyy} - Current date in MM/DD/YYYY format
 * - {privacy_link} - Privacy policy link
 * - {terms_of_use_link} - Terms of use link
 *
 * Note: Output is sanitized to prevent XSS attacks
 */
export const replaceDynamicTags = (
  text: string,
  replacements: TagReplacements = {}
): string => {
  if (!text) return "";

  // First sanitize the input to remove any malicious content
  let result = sanitizeInput(text);

  const now = new Date();
  const year = now.getFullYear().toString();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const dateFormatted = `${month}/${day}/${year}`;

  // Replace year tag
  result = result.replace(/{yyyy}/g, year);

  // Replace date tag
  result = result.replace(/{mm\/dd\/yyyy}/gi, dateFormatted);

  // Replace privacy link tag
  if (replacements.privacyLink) {
    result = result.replace(
      /{privacy_link}/g,
      `<a href="${replacements.privacyLink}" class="text-primary underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>`
    );
  } else {
    // Default to /privacy if no link provided
    result = result.replace(
      /{privacy_link}/g,
      '<a href="/privacy" class="text-primary underline">Privacy Policy</a>'
    );
  }

  // Replace terms of use link tag
  if (replacements.termsOfUseLink) {
    result = result.replace(
      /{terms_of_use_link}/g,
      `<a href="${replacements.termsOfUseLink}" class="text-primary underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>`
    );
  } else {
    // Default to /terms if no link provided
    result = result.replace(
      /{terms_of_use_link}/g,
      '<a href="/terms" class="text-primary underline">Terms of Service</a>'
    );
  }

  return result;
};

/**
 * Ensures opt-in language renders an actual link to both the Terms of Use and
 * Privacy Policy. Appends the {terms_of_use_link} / {privacy_link} tags for
 * whichever policy the text doesn't already link, so customized language still
 * surfaces both links once run through replaceDynamicTags.
 *
 * A policy counts as already linked only if the text contains its dynamic tag
 * or an anchor pointing at it — plain prose like "Privacy Policy" is not a link
 * and still gets a tag appended.
 */
export const withPolicyLinks = (text: string): string => {
  if (!text) return text;

  const hasTermsLink =
    /{terms_of_use_link}/.test(text) ||
    /<a\b[^>]*href=["'][^"']*terms/i.test(text);
  const hasPrivacyLink =
    /{privacy_link}/.test(text) ||
    /<a\b[^>]*href=["'][^"']*privacy/i.test(text);

  const needsTerms = !hasTermsLink;
  const needsPrivacy = !hasPrivacyLink;

  if (!needsTerms && !needsPrivacy) return text;

  const appended =
    needsTerms && needsPrivacy
      ? " {terms_of_use_link} and {privacy_link}."
      : needsTerms
        ? " {terms_of_use_link}."
        : " {privacy_link}.";

  return `${text.trimEnd()}${appended}`;
};

/**
 * Check if text contains any dynamic tags
 */
export const hasDynamicTags = (text: string): boolean => {
  if (!text) return false;
  return /{(yyyy|mm\/dd\/yyyy|privacy_link|terms_of_use_link)}/i.test(text);
};
